<template>
    <div
        ref="question"
        class="question-row question-row-toggle-picker"
        :data-lead-service-name="name"
    >
        <zoe-atom-toggle-picker
            :value="fieldValue"
            :left-option="{
                label: content.left_option.text.value,
                value: content.left_option.text.value,
            }"
            :right-option="{
                label: content.right_option.text.value,
                value: content.right_option.text.value,
            }"
            :handle-icon="content.handle_icon.value"
            @updateValue="updateValue"
        />
    </div>

    <div
        v-if="content.use_button.value"
        class="question-row question-row-button"
    >
        <button
            class="question-button-next button"
            type="button"
            value="next"
            data-showstopper="none"
            @click="check"
        >
            <zoe-atom-text
                :item="content.button.label.value"
            />
        </button>
    </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { addExtraData, getQuestionName, questionProps, storeExtraData } from '@mixins/quiz/v2.0.0/question';

import { checkCompleted } from '@mixins/quiz/v2.0.0/checks';
import { getNextQuestion } from '@mixins/quiz/v2.0.0/get-next-question';
import { setInvalid } from '@mixins/quiz/v2.0.0/invalid';

import ZoeAtomText from '@atoms/text/v1.0.0/zoe-atom-text.vue';
import ZoeAtomTogglePicker from '@molecules/toggle-picker/zoe-molecule-toggle-picker.vue';
import stripHtml from '@mixins/utils/strip-html';

const store = useStore();
const props = defineProps(questionProps);

const name = getQuestionName(props);
const uuid = ref(props.content?._info?.uuid);

const question = ref(null);
const fieldValue = ref('');

const options = {
    [props.content.left_option.text.value]: {
        text: props.content.left_option.text.value,
        showstopper: props.content.left_option.showstopper.value,
        hideShowstopperCloseButton: props.content.left_option.showstopper.hide_showstopper_close_button || false,
        next_question: props.content.left_option.next_question?.value,
        worktypeId: props.content.left_option.worktype_id_override?.value,
        enquiryDashboardRedirectVersion: props.content.left_option.enquiry_dashboard_redirect_version?.value,
    },

    [props.content.right_option.text.value]: {
        text: props.content.right_option.text.value,
        showstopper: props.content.right_option.showstopper.value,
        hideShowstopperCloseButton: props.content.right_option.showstopper.hide_showstopper_close_button || false,
        next_question: props.content.right_option.next_question?.value,
        worktypeId: props.content.right_option.worktype_id_override?.value,
        enquiryDashboardRedirectVersion: props.content.right_option.enquiry_dashboard_redirect_version?.value,
    },
};

const updateValue = value => {
    fieldValue.value = value;
    storeExtraData(name, 'label', value);
    storeExtraData(name, 'value', value);

    if (!props.content.use_button.value) {
        check();
    }
};

onMounted(() => {
    addExtraData(name, {
        name,
        value: '',
        mask: '',
        validator: '',
        uuid: uuid.value,
        question: stripHtml(props.content.text.value),
        sendData: props.content.send_to_lead_service?.value !== false,
    });
});

const check = () => {
    const pickedOption = options[fieldValue.value];

    const questionWrapper = question.value.closest('.question');
    const inputContainer = questionWrapper.querySelector('.question-row-toggle-picker');
    const togglePicker = questionWrapper.querySelector('.toggle-picker');

    const buttons = questionWrapper.querySelectorAll('[data-answer]');
    const delay = questionWrapper.querySelector('.button') ? 0 : 500;

    if (!pickedOption) {
        setInvalid(inputContainer);
        return;
    }

    if (pickedOption.showstopper !== '' && pickedOption.showstopper !== 'none') {
        if (pickedOption.hideShowstopperCloseButton === 'true') store.commit('dialog/preventClose');
        store.commit('dialog/show', pickedOption.showstopper);
        return;
    }

    const linkedFormInputIdentifier = togglePicker.dataset.linkedFormInput;
    const worktypeId = pickedOption.worktypeId;
    const enquiryDashboardRedirectVersion = pickedOption.enquiryDashboardRedirectVersion;
    const nextQuestion = getNextQuestion(pickedOption.next_question, store.state.quiz.currentQuestion);

    checkCompleted(name, buttons, props.preHook, nextQuestion, props.afterHook, fieldValue.value, linkedFormInputIdentifier, worktypeId, enquiryDashboardRedirectVersion, delay);
    storeExtraData(name, 'label', fieldValue.value);
    storeExtraData(name, 'value', fieldValue.value);
};
</script>

<style lang="scss">
@use 'zoe-quiz-toggle-picker';
</style>
